import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './customSelect.module.scss';
import CustomSelectProps from './CustomSelect.props';
import { Icon } from '@greystar/designsystem';

const CustomSelect = (props: Readonly<CustomSelectProps>) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const selectedOption = props.options.find((option) => option.leaseTerm === props.value);

  const renderSelectedValue = () => {
    const optionToDisplay =
      selectedOption ?? props.options.find((opt) => opt.leaseTerm === props.value);

    if (!optionToDisplay) {
      return <div className={styles['custom-select-placeholder']}>Select...</div>;
    }
    return (
      <div className={styles['custom-select-label']}>
        <span className={styles['custom-select-label-month']}>
          {`${optionToDisplay.leaseTerm} months (base rent)`}
        </span>
        <span className={styles['custom-select-label-price']}>
          {`$${optionToDisplay.price?.toLocaleString()}/month`}
        </span>
      </div>
    );
  };

  const handleToggleDropdown = useCallback(() => {
    if (!props.disabled) {
      setIsOpen((prev) => !prev);
    }
  }, [props.disabled]);

  const handleSelectOption = (optionValue: string) => {
    props.onChange(Number(optionValue));
    setIsOpen(false);
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
      setIsOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  return (
    <div className={styles['custom-select']} ref={selectRef} aria-disabled={props.disabled}>
      <button
        type="button"
        className={styles['custom-select-button']}
        onClick={handleToggleDropdown}
        disabled={props.disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {renderSelectedValue()}
        <span className={`${styles['custom-select-arrow']} ${isOpen ? styles['open'] : ''}`}>
          <Icon iconName="ArrowSmallDown" />
        </span>
      </button>
      {isOpen && (
        <div className={styles['options-list']} role="listbox">
          {props.options.map((option) => {
            const isSelected = option.leaseTerm === props.value;
            const optionClassName = `${styles['option-item']} ${
              isSelected ? styles['selected-option'] : ''
            }`;
            return (
              <div
                key={option.leaseTerm}
                className={optionClassName}
                onClick={() => handleSelectOption(option.leaseTerm)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleSelectOption(option.leaseTerm);
                  }
                }}
                role="option"
                aria-selected={isSelected}
                tabIndex={0}
              >
                <div className={styles['custom-select-label']}>
                  <span className={styles['custom-select-label-month']}>
                    {`${option.leaseTerm} months`}
                  </span>
                  <span className={styles['custom-select-label-price']}>
                    {`$${option.price.toLocaleString()}/month`}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;
